'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';

/**
 * The gray YouTube-style description card with expand/collapse —
 * eyebrow header line in bold, body text clamped to a few lines,
 * optional director credit + hashtag chips, and a {@code ...more} /
 * {@code Show less} toggle when the body exceeds the line budget.
 *
 * <p>Why a component instead of inline: three watch surfaces today
 * render this card (standalone video, series detail with auto-loaded
 * episode 1, in-series watch). All three need to clamp/expand
 * identically. Keeping the expand state local here keeps the parent
 * layouts pure-presentational.
 *
 * <h3>Visual model</h3>
 *
 * <p>Collapsed:
 * <pre>{@code
 *   ┌──────────────────────────────────────┐
 *   │ 4.2M views · 2023 · 5 × 26 min       │  ← header (bold)
 *   │                                       │
 *   │ Five half-hour episodes about the     │  ← body, line-clamped
 *   │ people who work above 3,000 metres.   │     to maxCollapsedLines
 *   │                                       │
 *   │ ...more                               │  ← toggle
 *   └──────────────────────────────────────┘
 * }</pre>
 *
 * <p>Expanded: full body + optional {@code directorLine} + hashtag
 * chips for {@code tags}.
 *
 * <p>The toggle is only rendered when the body actually exceeds the
 * collapsed-line budget — short descriptions don't show a misleading
 * {@code ...more} affordance. Because layout can't be measured from a
 * Server Component, the rule is heuristic: if the body string is
 * longer than {@code collapseAtChars} (default 280, ≈ 4-5 short
 * lines), the toggle renders.
 */
interface Props {
  /** Bold header line — typically
   *  {@code "{compactViews} views · {year} · {runtime}"}. */
  header: ReactNode;
  /** The long-form description. Empty/undefined → component renders nothing. */
  body?: string;
  /** Tags rendered as hashtag chips below the body and below the
   *  {@code directorLine}. Empty/undefined → no chip row. */
  tags?: string[];
  /** Optional {@code Directed by X} line below the body. Hidden in
   *  collapsed mode (always sits in the expanded portion). */
  directorLine?: ReactNode;
  /** Character threshold above which the {@code ...more} toggle
   *  appears. Default 280 — roughly 4-5 lines at typical widths. */
  collapseAtChars?: number;
  /** Lines to show before clamping when collapsed. Default 3. */
  maxCollapsedLines?: number;
}

export function CollapsibleDescription({
  header,
  body,
  tags,
  directorLine,
  collapseAtChars = 280,
  maxCollapsedLines = 3,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  if (!body && !tags?.length && !directorLine) return null;

  const showToggle = !!body && body.length > collapseAtChars;
  const bodyStyle =
    showToggle && !expanded
      ? {
          display: '-webkit-box' as const,
          WebkitLineClamp: maxCollapsedLines,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden' as const,
        }
      : undefined;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CD_STYLES }} />

      <div className="cd-card">
        <div className="cd-head">{header}</div>

        {body && (
          <div className="cd-body" style={bodyStyle}>
            {body}
          </div>
        )}

        {expanded && directorLine && <div className="cd-director">{directorLine}</div>}
        {expanded && tags && tags.length > 0 && (
          <div className="cd-tags">
            {tags.map((t, i) => (
              <span key={i}>#{t.toLowerCase().replace(/\s+/g, '')}</span>
            ))}
          </div>
        )}

        {showToggle && (
          <button
            type="button"
            className="cd-toggle"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
          >
            {expanded ? 'Show less' : '...more'}
          </button>
        )}
      </div>
    </>
  );
}

const CD_STYLES = `
  .cd-card {
    margin-top: 16px;
    padding: 12px 16px 16px;
    border-radius: 12px;
    background: var(--color-neutral-100, #f2f2f2);
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-text-primary, #0f0f0f);
  }
  .cd-head {
    font-weight: 600;
    margin-bottom: 8px;
  }
  .cd-body {
    white-space: pre-wrap;
  }
  .cd-director {
    margin-top: 12px;
    font-size: 13px;
    color: var(--color-text-secondary, #606060);
  }
  .cd-tags {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    color: var(--color-brand-500, #065fd4);
    font-weight: 500;
  }
  .cd-toggle {
    display: inline-block;
    margin-top: 8px;
    background: transparent;
    border: none;
    padding: 4px 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary, #0f0f0f);
    cursor: pointer;
  }
  .cd-toggle:hover {
    text-decoration: underline;
  }
`;
